import produce from 'immer';
import { create } from 'zustand';

import type { NodeDeletionResult } from 'api/data/client-dataapi';
import { normalizeProposalId } from 'api/data/events/normalizeProposalId';
import type { DefaultCasedOutputOperationResult } from 'api/outputOperationResult';
import invertBy from 'lodash/invertBy';
import type { DataNodeDeletedEvent, EventCompletion } from 'state/signalr/signalrState';

export type NodeDeletionPhase =
  | 'idle'
  | 'userWaitsForDeletionResult'
  | 'deletionFailed'
  | 'deletionComplete'
  | 'awaitUserApproval';

export interface NodeDeletionState {
  deletingNodeId: string | undefined;
  deletionEventId: string | undefined;

  /**
   * Human-readable name of the node being deleted, supplied by the caller that started the deletion.
   * The confirmation dialog otherwise looks the name up in the legacy GRID_STORE, which only holds
   * rows loaded by the node-query grid — callers with their own row source (the project nodes grid)
   * pass it here so the dialog isn't titled "Unknown".
   */
  deletingNodeDisplayName: string | undefined;

  clearedNodes: { [key: string]: string[] };
  clearedRelationships: { [key: string]: string[] };

  completedDeletionResult: NodeDeletionResult | undefined;
  blockedDeletionResult: NodeDeletionResult | undefined;

  originatingDeletionEvent: DataNodeDeletedEvent | undefined;

  waitForEventCompletion: (event: DataNodeDeletedEvent, displayName?: string) => void;
  processDeletionResult: (
    event: EventCompletion<DataNodeDeletedEvent, DefaultCasedOutputOperationResult<NodeDeletionResult>>
  ) => void;

  clear: () => void;

  deletionState: NodeDeletionPhase;
  deletionError: string | undefined;
}

export const DELETE_NODE_STATE = create<NodeDeletionState>((set) => ({
  clearedNodes: {},
  clearedRelationships: {},
  deletingNodeId: undefined,
  deletingNodeDisplayName: undefined,
  deletionEventId: undefined,
  blockedDeletionResult: undefined,
  originatingDeletionEvent: undefined,
  completedDeletionResult: undefined,
  deletionState: 'idle',
  deletionError: undefined,
  waitForEventCompletion: (event: DataNodeDeletedEvent, displayName?: string) =>
    set((state) => {
      const originatingDeletionEvent = { ...event, ProposalId: normalizeProposalId(event.ProposalId) };
      const newState = produce(state, (draft) => {
        // Keep the name across confirmation rounds: confirming a blocking cascade re-issues the
        // delete through gridActions.DeleteNode, which passes no display name. Retain the existing
        // one when this is the same node being re-issued; any other case takes the new value
        // (including undefined, so a caller that has no name can't inherit a stale one).
        // Compared against `state`, not `draft`, because `deletingNodeId` is overwritten just below.
        const isSameNodeReissue = state.deletingNodeId === originatingDeletionEvent.DataNodeId;
        if (displayName !== undefined || !isSameNodeReissue) {
          draft.deletingNodeDisplayName = displayName;
        }

        draft.deletionEventId = originatingDeletionEvent.EventId;
        draft.deletingNodeId = originatingDeletionEvent.DataNodeId;
        draft.originatingDeletionEvent = originatingDeletionEvent;
        draft.deletionState = 'userWaitsForDeletionResult';
      });

      return newState;
    }),
  processDeletionResult: (
    q: EventCompletion<DataNodeDeletedEvent, DefaultCasedOutputOperationResult<NodeDeletionResult>>
  ) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const event = q.Result.Result;

        if (state.deletionEventId === q.DataEvent.EventId) {
          if (!q.Result.Success) {
            draft.deletionState = 'deletionFailed';
            draft.deletionError =
              q.Result.Error === 'INSUFFICIENT_PERMISSIONS'
                ? 'Insufficient permissions for deleting the instance or its dependencies.'
                : 'Error deleting data instance. Please try again or contact support.';

            return;
          }

          // when the deletion is done
          if (!event.NeedsConfirmation) {
            draft.deletionEventId = undefined;
            draft.clearedNodes = {};
            draft.clearedRelationships = {};
            draft.deletingNodeId = undefined;
            draft.deletingNodeDisplayName = undefined;
            draft.blockedDeletionResult = undefined;
            draft.completedDeletionResult = event;
            // Deliberately NOT cleared here (only ever overwritten by the next `waitForEventCompletion`):
            // `NodeDeletionIndicator` reads it on this same transition to know which node type's row
            // cache to invalidate, since the `ProjectNodesTable` instance that started the deletion may
            // already have unmounted (the user switched node type) by the time this reply arrives.
            draft.deletionState = 'deletionComplete';
            draft.deletionError = undefined;
          } else {
            const clearedNodes = {} as { [key: string]: string[] };
            const invertedNodeMetaMap = invertBy(event.NodeDescriptionMap); // queryNodeId [nodeKeys]

            Object.values(event.NodeDescriptions).forEach((desc) => {
              if (desc.IsBlocked) {
                clearedNodes[desc.TemplateNodeId] = invertedNodeMetaMap[desc.Id];
              }
            });

            draft.clearedNodes = clearedNodes;

            const clearedRelations = {} as { [key: string]: string[] };
            const invertedRelationMetaMap = Object.fromEntries(
              Object.entries(event.RelationDescriptionMap).map(([key, val]) => [val, key])
            );

            Object.values(event.RelationDescriptions).forEach((desc) => {
              if (desc.IsBlocked) {
                if (!clearedRelations[desc.TemplateEdgeId]) {
                  clearedRelations[desc.TemplateEdgeId] = [];
                }

                // 2. Push the new value
                const metaValue = invertedRelationMetaMap[desc.Id];
                clearedRelations[desc.TemplateEdgeId].push(metaValue);
              }
            });

            draft.clearedRelationships = clearedRelations;
            draft.blockedDeletionResult = event;
            draft.completedDeletionResult = undefined;
            draft.deletionState = 'awaitUserApproval';
            draft.deletionError = undefined;
          }
        }
      });

      return newState;
    }),
  clear: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.deletionEventId = undefined;
        draft.clearedNodes = {};
        draft.clearedRelationships = {};
        draft.deletingNodeId = undefined;
        draft.deletingNodeDisplayName = undefined;
        draft.deletionState = 'idle';
        draft.deletionError = undefined;
      });

      return newState;
    })
}));
